import {ErrorMessage, ImageRadioGroup, LinkButton, Loader} from '@communityshare/ui-shared';
import {useConnect} from '@lore/query-connect';
import {PayloadStates} from '@lore/utils';
import {useConfig} from '@lore/config';
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {PhotoIcon} from '@heroicons/react/24/outline';
import useProjectImage from '@/features/share-project/hooks/useProjectImage';
import usePreviewImages from '@/features/share-project/hooks/usePreviewImages';
import getProjectShareUrl from '@/features/share-project/utils/getProjectShareUrl';

// Window name for the preview tab: repeat clicks reuse the tab instead of
// opening new ones.
const PREVIEW_WINDOW_NAME = 'cs-share-preview';

LinkPreview.propTypes = {
  project: PropTypes.object.isRequired
};

export default function LinkPreview(props) {
  const { project } = props;
  const { t } = useTranslation();
  const config = useConfig();
  // Default to tag 1 so byId never throws for projects without an activity
  const activity = useConnect('tag.byId', {
    id: project?.data?.activityId ?? 1
  });
  const siteId = project?.data?.siteId ?? null;
  // Pass 0 when siteId is absent, so the blueprint doesn't throw 'Missing id';
  // a 0-id query returns NOT_FOUND harmlessly. Location is omitted in the
  // render when siteId is null (see below).
  const site = useConnect('site.byId', {
    id: siteId ?? 0
  });
  // Local selection; null defers to the saved image (or the default)
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [imageErrored, setImageErrored] = useState(false);
  const projectImage = useProjectImage(project.id);
  const { loading: imagesLoading, images } = usePreviewImages(project.data.activityId);
  const defaultSavedRef = useRef(false);

  const shareUrl = getProjectShareUrl(config, project);
  const shareHostname = new URL(shareUrl).hostname;

  // A project with no saved image gets the default persisted once,
  // so the share page renders the same photo the preview shows.
  // Waits for the activity's image set so the right default is saved.
  useEffect(() => {
    if (projectImage.isSuccess && !projectImage.image && !imagesLoading && !defaultSavedRef.current) {
      defaultSavedRef.current = true;
      const image = images[0];
      projectImage.upsert({ imageUrl: image.src, width: image.width, height: image.height });
    }
  }, [projectImage.isSuccess, projectImage.image, imagesLoading, images]);

  // loading project, it's saved share image, or the activity's image set
  if ([PayloadStates.FETCHING, PayloadStates.INITIAL_STATE].includes(project.state) || projectImage.isLoading || imagesLoading) {
    return (
      <div className="flex flex-col justify-center h-[80vh]">
        <Loader/>
      </div>
    )
  }

  // unexpected error state
  if (![PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(project.state) || !project.data) {
    return (
      <div className="flex flex-col items-center">
        <ErrorMessage v2={true}>
          Error getting project data
        </ErrorMessage>
      </div>
    )
  }

  // Local selection wins (optimistic), then the saved image;
  // null renders the PhotoIcon empty state until an image exists
  const savedImage = images.find(image => image.src === projectImage.image?.imageUrl);
  const shareImage = images.find(image => image.id === selectedImageId) ?? savedImage ?? null;

  // The preview swaps immediately while the save runs;
  // a failure surfaces through projectImage.upsertError below.
  function onShareImageChange(imageId) {
    setSelectedImageId(imageId);
    setImageErrored(false);
    const image = images.find(image => image.id === imageId);
    projectImage.upsert({
      imageUrl: image.src,
      width: image.width,
      height: image.height
    });
  }

  function onPreview() {
    window.open(shareUrl, `${PREVIEW_WINDOW_NAME}-${project.id}`);
  }

  // project is loaded or updating (probably saving the title)
  return (
    <div className="bg-white border border-cs-gray-200 rounded-cs-10 p-5">
      <h3 className="text-xl font-semibold text-cs-gray-900 mb-4">
        {t('project_share.link_preview')}
      </h3>
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* overflow-hidden clips the image to the card's rounded corners */}
        <div className="flex-1 min-w-0 bg-white border border-cs-gray-200 rounded-cs-10 overflow-hidden">
          {shareImage && !imageErrored ? (
            // key remounts the img on swap so the fade-in animation replays
            <img
              id={`share-image-${shareImage.id}`}
              key={shareImage.id}
              className="w-full aspect-[16/9] object-cover animate-fade-in"
              src={shareImage.src}
              alt={t('project_share.preview_image_alt')}
              // Curated WordPress URLs can 404; fall back to the empty-state placeholder.
              onError={() => setImageErrored(true)}
            />
          ) : (
            // Empty state: no image saved yet, or the saved image failed to load
            <div className="w-full aspect-[16/9] flex items-center justify-center bg-cs-gray-100 animate-fade-in">
              <PhotoIcon className="size-16 text-cs-gray-400" aria-hidden="true" />
            </div>
          )}
          <div className="px-5">
            <p className="mt-3 text-sm text-cs-gray-500">
              {shareHostname}/share/project/{project.id}
            </p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-cs-orange-500">
              {activity.state === PayloadStates.FETCHING && <Loader/>}
              {activity?.data?.name ?? '...'}{siteId && site.state !== PayloadStates.FETCHING ? ` | ${site?.data?.city ?? ''}, ${site?.data?.state ?? ''}` : ''}
            </p>
            <p className="mt-1 text-base font-semibold text-cs-gray-900">
              {t('project_share.cta_line')}
            </p>
            <p className="mt-1 text-sm italic text-cs-gray-500">
              &ldquo;{project.data.title}&rdquo;
            </p>
            <div className="mt-2 pt-2 border-t border-cs-gray-200 flex justify-end">
              <LinkButton label={t('project_share.preview_page')} icon="right" onClick={onPreview}/>
            </div>
          </div>
        </div>
        <ImageRadioGroup
          label={t('project_share.swap_photo')}
          images={images}
          value={shareImage?.id ?? null}
          onChange={onShareImageChange}
          getImageLabel={(image, index) => t('project_share.photo_option', { number: index + 1 })}
          className="lg:w-36 lg:flex-shrink-0"
          optionsClassName="flex gap-2 lg:flex-col"
          optionClassName="flex-1 min-w-0 lg:flex-none"
        />
      </div>
      {projectImage.upsertError && (
        <div className="mt-4">
          <ErrorMessage v2={true}>
            {t('project_share.image_error')}
          </ErrorMessage>
        </div>
      )}
    </div>
  );
}
